// MUI base styles
import colors from "../../assets/theme/base/colors";
import typography from "../../assets/theme/base/typography";
import borders from "../../assets/theme/base/borders";
import boxShadows from "../../assets/theme/base/boxShadows";

// MUI helper functions
import pxToRem from "../../assets/theme/functions/pxToRem";
import boxShadow from "../../assets/theme/functions/boxShadow";
import rgba from "../../assets/theme/functions/rgba";

// @emotion/react components
import { keyframes, Interpolation, Theme } from "@emotion/react";

interface StyleProps {
	selectSize: "small" | "medium" | "large";
	selectError?: boolean;
	selectSuccess?: boolean;
}

interface CustomTheme extends Theme {
	colors: typeof colors;
	typography: typeof typography;
	borders: typeof borders;
	boxShadows: typeof boxShadows;
}

const selectStyles = (
	selectSize: "small" | "medium" | "large",
	selectError?: boolean,
	selectSuccess?: boolean
) => {
	const {
		dark,
		white,
		text,
		light,
		inputColors,
		gradients,
		transparent,
		background,
	} = colors;
	const { size, fontWeightRegular } = typography;
	const { borderWidth, borderRadius } = borders;
	const { inputBoxShadow, lg, sm } = boxShadows;

	let borderColorValue = inputColors.borderColor.main;
	let borderColorFocused = inputColors.borderColor.focus;
	let boxShadowValue: string;
	let selectSizeValue: string;

	if (selectError) {
		borderColorValue = inputColors.error;
		borderColorFocused = inputColors.error;
	} else if (selectSuccess) {
		borderColorValue = inputColors.success;
		borderColorFocused = inputColors.success;
	} else {
		borderColorValue = inputColors.borderColor.main;
		borderColorFocused = inputColors.borderColor.focus;
	}

	if (selectError) {
		boxShadowValue = `${boxShadow(
			[0, 3],
			[9, 0],
			inputColors.error,
			0
		)}, ${boxShadow([3, 4], [8, 0], inputColors.error, 0.1)}`;
	} else if (selectSuccess) {
		boxShadowValue = `${boxShadow(
			[0, 3],
			[9, 0],
			inputColors.success,
			0
		)}, ${boxShadow([3, 4], [8, 0], inputColors.success, 0.1)}`;
	} else {
		boxShadowValue = inputBoxShadow;
	}

	if (selectSize === "small") {
		selectSizeValue = pxToRem(32);
	} else if (selectSize === "large") {
		selectSizeValue = pxToRem(48);
	} else {
		selectSizeValue = pxToRem(40);
	}

	// animations
	const prespective = keyframes`
        from {
            opacity: 0;
            transform: perspective(999px) rotateX(-10deg) translateZ(0) translate3d(0, 0, 0);
        }
        to {
            opacity: 1;
            transform: perspective(999px) rotateX(0deg) translateZ(0) translate3d(0, 0, 5px);
        }
    `;

	return {
		control: (provided, state) => ({
			display: "flex",
			alignItems: "center",
			width: "100%",
			height: "auto",
			padding: 0,
			fontSize: selectSize === "small" ? size.xs : size.sm,
			fontWeight: fontWeightRegular,
			backgroundColor: white.main,
			backgroundClip: "padding-box",
			border: `${borderWidth[1]} solid`,
			appearance: "none",
			borderRadius: borderRadius.sm,
			transition: "box-shadow 150ms ease, border-color 150ms ease",
			cursor: "pointer",
			borderColor: borderColorValue,
			boxShadows: sm,
			// borderColor: state.isFocused ? borderColorFocused : borderColorValue,
			// boxShadow: state.isFocused ? boxShadowValue : "none",
		}),

		valueContainer: () => ({
			display: "flex",
			alignItems: "center",
			width: "100%",
			height: "max-content",
			padding: `0 ${pxToRem(12)}`,
			color: dark.main,
			flexWrap: "wrap",
		}),

		placeholder: () => ({
			position: "absolute",
			top: selectSize === "small" ? "52%" : "51%",
			transform: "translateY(-50%)",
			marginLeft: 0,
			marginRight: 0,
			color: dark.main,
			opacity: 0.5,
		}),

		singleValue: () => ({
			position: "absolute",
			top: selectSize === "small" ? "52%" : "51%",
			transform: "translateY(-50%)",
			color: text.main,
		}),

		input: () => ({
			color: dark.main,
			position: "relative",
			margin: 0,
		}),

		indicatorSeparator: () => ({
			display: "none",
		}),

		dropdownIndicator: (provided, state) => ({
			display: "flex",
			padding: pxToRem(12),

			"& svg": {
				fill: text.main,
				width: pxToRem(14),
				height: pxToRem(14),
				strokeWidth: 1,
				stroke: text.main,
				transform: state.selectProps.menuIsOpen
					? "rotate(180deg)"
					: "rotate(0)",
				transition: "transform 250ms ease",
			},
		}),

		menu: (provided) => ({
			...provided,
			minWidth: pxToRem(160),
			boxShadow: lg,
			padding: `${pxToRem(16)} ${pxToRem(8)}`,
			fontSize: size.sm,
			color: text.main,
			textAlign: "left",
			backgroundColor: white.main,
			borderRadius: borderRadius.sm,
			transformOrigin: "50% 0",
			animation: `${prespective} 250ms ease forwards !important`,
		}),

		menuList: (provided) => ({
			...provided,
			padding: 0,
		}),

		option: (provided, state) => ({
			...provided,
			position: "relative",
			minWidth: pxToRem(160),
			minHeight: "unset",
			padding: `${pxToRem(4.8)} ${pxToRem(100)} ${pxToRem(4.8)} ${pxToRem(16)}`,
			borderRadius: borderRadius.sm,
			fontSize: size.sm,
			backgroundColor: state.isSelected
				? rgba(light.main, 1)
				: transparent.main,
			color: text.main,
			cursor: state.isDisabled ? "not-allowed" : "pointer",
			opacity: state.isDisabled ? 0.5 : 1,
			userSelect: state.isDisabled ? "none" : "auto",
			transition: "background-color 300ms ease, color 300ms ease",

			"&:after": {
				content: "'Press to select'",
				display: "block",
				fontSize: size.xs,
				position: "absolute",
				right: pxToRem(10),
				top: "52%",
				color: text.main,
				opacity: 0,
				transform: "translateY(-50%)",
				transition: "opacity 300ms ease",
			},

			"&:hover, &:focus": {
				backgroundColor: state.isDisabled
					? transparent.main
					: rgba(light.main, 1),
				color: state.isDisabled ? "currentColor" : dark.main,

				"&:after": {
					content: state.isDisabled && "''",
					opacity: 0.5,
				},
			},
		}),

		multiValue: (provided) => ({
			...provided,
			margin: `${pxToRem(4)} ${pxToRem(6)} ${pxToRem(4)} 0`,
			borderRadius: borderRadius.md,
			display: "flex",
			alignItems: "center",
			backgroundColor: rgba(light.main, 0.85),
			color: dark.main,
			boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
			padding: `${pxToRem(3)} ${pxToRem(8)} ${pxToRem(3)} ${pxToRem(8)}`,
			fontWeight: 500,
			fontSize: size.sm,
			transition: "background 0.2s, color 0.2s",

			"& div:first-of-type": {
				color: dark.main,
				paddingTop: pxToRem(2),
				paddingBottom: pxToRem(2),
				fontWeight: 500,
			},

			"& div:last-of-type": {
				marginLeft: pxToRem(6),
				padding: 0,
				opacity: 0.7,
				borderRadius: "50%",
				width: pxToRem(20),
				height: pxToRem(20),
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				background: "none",
				transition: "background 0.2s, color 0.2s",
				cursor: "pointer",
				"&:hover": {
					backgroundColor: rgba(dark.main, 0.08),
					color: dark.main,
					opacity: 1,
				},
			},
		}),
	};
};

export default selectStyles;
